import React, { useEffect, useState } from "react";
import { api, resolveImageUrl } from "../../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Save } from "lucide-react";

export const STATUS_STYLES = {
  in_stock: "bg-green-500/15 text-green-300 border-green-500/40",
  low_stock: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  out_of_stock: "bg-red-500/15 text-red-300 border-red-500/40",
  untracked: "bg-white/5 text-gray-500 border-white/15",
};
export const STATUS_LABELS = {
  in_stock: "IN STOCK",
  low_stock: "LOW STOCK",
  out_of_stock: "OUT OF STOCK",
  untracked: "NOT TRACKED",
};

export function StatusBadge({ status, testid }) {
  return (
    <span data-testid={testid}
          className={`inline-block border px-2 py-0.5 text-[10px] font-mono-alt tracking-widest uppercase ${STATUS_STYLES[status] || STATUS_STYLES.untracked}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function InventoryTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { limit: 200 } });
      setProducts(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>;

  return (
    <div data-testid="inventory-tab">
      <div className="mb-6">
        <h2 className="font-display text-2xl uppercase tracking-tight">Inventory</h2>
        <p className="text-xs text-gray-500 mt-1">
          Flower &amp; wax are tracked in ounces (enter stock like "2 LB + 1 QP"). Carts &amp; edibles are tracked in units.
          Stock is deducted when an order is confirmed and restored if it's cancelled.
        </p>
      </div>
      <div className="border border-white/10">
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-gray-500">
          <div className="col-span-4">Product</div>
          <div className="col-span-4">Current stock</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Manage</div>
        </div>
        {products.map((p) => (
          <div key={p.id} className="border-b border-white/5">
            <div className="grid grid-cols-12 gap-3 items-center px-4 py-3 text-sm" data-testid={`inv-row-${p.id}`}>
              <div className="col-span-8 md:col-span-4 flex items-center gap-3 min-w-0">
                <img src={resolveImageUrl(p.image)} alt="" className="w-10 h-10 object-cover border border-white/10" />
                <div className="min-w-0">
                  <p className="font-display uppercase truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">{p.category}</p>
                </div>
              </div>
              <div className="hidden md:block col-span-4">
                {p.inventory_qty != null ? (
                  <>
                    <p className="text-white font-display" data-testid={`inv-display-${p.id}`}>{p.inventory_display}</p>
                    {p.inventory_unit === "oz" && (
                      <p className="text-[10px] text-gray-500">{p.inventory_qty} OZ total</p>
                    )}
                  </>
                ) : (
                  <span className="text-gray-600 text-xs uppercase tracking-widest">Not tracked — set stock to start</span>
                )}
              </div>
              <div className="hidden md:block col-span-2">
                <StatusBadge status={p.stock_status} testid={`inv-status-${p.id}`} />
              </div>
              <div className="col-span-4 md:col-span-2 flex justify-end">
                <button
                  data-testid={`inv-manage-${p.id}`}
                  onClick={() => setOpenId(openId === p.id ? null : p.id)}
                  className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[var(--hk-orange)] px-3 py-1.5 text-xs uppercase tracking-widest"
                >
                  {openId === p.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Manage
                </button>
              </div>
            </div>
            {openId === p.id && (
              <InventoryPanel product={p} onChanged={load} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function InventoryPanel({ product, onChanged }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => { setRefreshKey((k) => k + 1); onChanged(); };
  return (
    <div className="px-4 pb-6 pt-2 bg-white/[0.02] border-t border-white/5" data-testid={`inv-panel-${product.id}`}>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <AdjustForm product={product} onDone={bump} />
          <ThresholdEditor product={product} onDone={bump} />
        </div>
        <div className="space-y-5">
          <Stats productId={product.id} refreshKey={refreshKey} />
          <TxnList productId={product.id} refreshKey={refreshKey} />
        </div>
      </div>
    </div>
  );
}

const ADJUST_VERB = { set: "set", add: "added", remove: "removed" };

function AdjustForm({ product, onDone }) {
  const isOz = product.inventory_unit === "oz";
  const [entry, setEntry] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOz || !entry.trim()) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.post("/admin/inventory/parse", { text: entry, unit: "oz" })
        .then((r) => setPreview({ ok: true, ...r.data }))
        .catch((e) => setPreview({ ok: false, error: e.response?.data?.detail || "Can't parse" }));
    }, 350);
    return () => clearTimeout(t);
  }, [entry, isOz]);

  const submit = async (action) => {
    if (!entry.trim()) { toast.error("Enter a quantity first"); return; }
    const labels = { add: "Add", remove: "Remove", set: "Set stock to" };
    const confirmMsg = preview?.ok
      ? `${labels[action]} ${preview.display}?`
      : `${labels[action]} ${entry}?`;
    if (action === "set" && !window.confirm(`${confirmMsg}\nThis REPLACES the current stock (recorded as an inventory correction).`)) return;
    setBusy(true);
    try {
      const payload = isOz
        ? { action, entry, notes: notes || null }
        : { action, amount: Number(entry), notes: notes || null };
      const { data } = await api.post(`/admin/inventory/${product.id}/adjust`, payload);
      toast.success(`Inventory ${ADJUST_VERB[action]} — now ${data.product.inventory_display}`);
      setEntry(""); setNotes(""); setPreview(null);
      onDone();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="border border-white/10 p-4">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-3">Adjust stock</p>
      <input
        data-testid={`inv-entry-${product.id}`}
        value={entry}
        onChange={(e) => setEntry(e.target.value)}
        placeholder={isOz ? 'e.g. "2 LB + 1 QP" or "4 oz"' : "Number of units, e.g. 100"}
        type={isOz ? "text" : "number"}
        className="w-full bg-transparent border border-white/15 focus:border-[var(--hk-orange)] outline-none px-3 py-2 text-white placeholder-gray-600 text-sm"
      />
      {isOz && preview && (
        <p data-testid={`inv-preview-${product.id}`}
           className={`text-xs mt-2 ${preview.ok ? "text-[var(--hk-orange)]" : "text-red-400"}`}>
          {preview.ok ? `= ${preview.display} (${preview.qty} OZ)` : preview.error}
        </p>
      )}
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="w-full bg-transparent border-b border-white/10 focus:border-white/40 outline-none px-1 py-2 mt-2 text-xs text-gray-300 placeholder-gray-600"
      />
      <div className="flex flex-wrap gap-2 mt-4">
        <button disabled={busy} data-testid={`inv-add-${product.id}`} onClick={() => submit("add")}
                className="bg-green-500/10 border border-green-500/40 text-green-300 hover:bg-green-500/20 px-4 py-2 text-xs uppercase tracking-widest font-display font-bold disabled:opacity-50">
          + Add stock
        </button>
        <button disabled={busy} data-testid={`inv-remove-${product.id}`} onClick={() => submit("remove")}
                className="bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 px-4 py-2 text-xs uppercase tracking-widest font-display font-bold disabled:opacity-50">
          − Remove
        </button>
        <button disabled={busy} data-testid={`inv-set-${product.id}`} onClick={() => submit("set")}
                className="border border-white/20 text-white hover:border-[var(--hk-orange)] px-4 py-2 text-xs uppercase tracking-widest font-display font-bold disabled:opacity-50">
          Set exact stock
        </button>
      </div>
    </div>
  );
}

function ThresholdEditor({ product, onDone }) {
  const [val, setVal] = useState(product.low_stock_threshold ?? "");
  const save = async () => {
    const num = Number(val);
    if (val === "" || Number.isNaN(num) || num < 0) { toast.error("Enter a valid threshold"); return; }
    try {
      await api.put(`/admin/products/${product.id}`, { low_stock_threshold: num });
      toast.success("Low-stock threshold saved");
      onDone();
    } catch {
      toast.error("Failed to save threshold");
    }
  };
  return (
    <div className="border border-white/10 p-4">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-2">Low stock alert threshold</p>
      <div className="flex items-center gap-2">
        <input
          data-testid={`inv-threshold-${product.id}`}
          type="number" min="0" step={product.inventory_unit === "oz" ? "0.125" : "1"}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={product.inventory_unit === "oz" ? "16" : "50"}
          className="w-28 bg-transparent border border-white/15 focus:border-[var(--hk-orange)] outline-none px-3 py-2 text-white text-sm"
        />
        <span className="text-xs text-gray-500 uppercase tracking-widest">{product.inventory_unit === "oz" ? "OZ" : "UNITS"}</span>
        <button data-testid={`inv-threshold-save-${product.id}`} onClick={save}
                className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[var(--hk-orange)] px-3 py-2 text-xs uppercase tracking-widest">
          <Save size={12} /> Save
        </button>
      </div>
      <p className="text-[10px] text-gray-600 mt-2">Product shows LOW STOCK at or below this amount. Default: {product.inventory_unit === "oz" ? "16 OZ" : "50 units"}.</p>
    </div>
  );
}

function Stats({ productId, refreshKey }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get(`/admin/inventory/${productId}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, [productId, refreshKey]);
  if (!stats) return null;
  const rows = [
    ["Current", stats.current_display ?? "—"],
    ["Starting", stats.starting_display ?? "—"],
    ["Added", stats.added_display ?? "0"],
    ["Sold", stats.sold_display ?? "0"],
    ["Returned", stats.returned_display ?? "0"],
    ["Sets & corrections", stats.manual_display ?? "0"],
  ];
  return (
    <div className="border border-white/10 p-4" data-testid={`inv-stats-${productId}`}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-3">Statistics</p>
      <div className="grid grid-cols-3 gap-3">
        {rows.map(([label, value]) => (
          <div key={label}>
            <p className="text-[10px] uppercase tracking-widest text-gray-600">{label}</p>
            <p className="text-sm text-white font-display">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const TXN_COLORS = {
  SALE: "text-red-300", OVERRIDE: "text-red-400", MANUAL_REMOVE: "text-red-300",
  RESTOCK: "text-green-300", RETURN: "text-green-300", CANCELLATION: "text-green-300",
  INVENTORY_SET: "text-amber-300", ORDER_EDIT: "text-amber-300", CORRECTION: "text-amber-300",
};

function TxnList({ productId, refreshKey }) {
  const [txns, setTxns] = useState([]);
  useEffect(() => {
    api.get("/admin/inventory/transactions", { params: { product_id: productId, limit: 30 } })
      .then((r) => setTxns(r.data || []))
      .catch(() => setTxns([]));
  }, [productId, refreshKey]);
  return (
    <div className="border border-white/10 p-4" data-testid={`inv-txns-${productId}`}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-3">Transaction history</p>
      {txns.length === 0 ? (
        <p className="text-xs text-gray-600">No inventory transactions yet.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {txns.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 border-b border-white/5 pb-2 text-xs">
              <div className="min-w-0">
                <p>
                  <span className={`font-mono-alt tracking-wider ${TXN_COLORS[t.type] || "text-gray-300"}`}>{t.type}</span>
                  <span className="text-white ml-2">{t.change_display}</span>
                </p>
                <p className="text-gray-600 mt-0.5">
                  {t.prev_display ?? "—"} → {t.new_display}
                  {t.order_number ? ` · #${t.order_number}` : ""}
                  {t.notes ? ` · ${t.notes}` : ""}
                </p>
              </div>
              <span className="text-gray-600 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
